import { POLLING_FREQUENCIES, PollingRule } from "./constants";
import { getHighestActiveFrequency } from "./polling-rules";
import { refreshChangedDevices } from "./refresh-devices";

/**
 * Device polling service — polls for changed devices at the fastest frequency across all active rules.
 *
 * - Rules are additive: all device ids from all active rules are polled together.
 * - The service auto-starts when the first rule is added.
 * - Each rule can optionally supply an onTick callback to dynamically control whether
 *   the rule is active and which device ids to include.
 */

// === State ===

let rules: PollingRule[] = [];

/** Timeout id for the next scheduled poll. Null when no timeout is pending. */
let timeoutId: number | null = null;

/** True while a poll is awaiting a network response. */
let isPolling = false;

/** Current scheduled interval in ms. */
let currentIntervalMs: number | null = null;

let isRunning = false;

// === Internal ===

function clearScheduledTimeout(): void {
    if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
    }
}

/**
 * Schedule the next poll after delayMs. No-ops if already scheduled at the same interval.
 */
function scheduleNext(delayMs: number): void {
    if (!isRunning) return;
    // If a poll is in-flight, return immediately — runThenSchedule will reschedule at the correct frequency after the poll
    if (isPolling) return;
    if (delayMs === currentIntervalMs && timeoutId !== null) return;
    clearScheduledTimeout();
    currentIntervalMs = delayMs;
    timeoutId = window.setTimeout(runThenSchedule, delayMs);
}

/**
 * Evaluates all rules in a single pass so each rule's onTick is called exactly once
 * when determining which devices to fetch.
 *
 * A separate evaluation happens after the poll completes in runThenSchedule to pick up any rule changes
 * that arrived mid-poll.
 */
async function pollDevices(): Promise<void> {
    const evaluated = rules.map((rule) => ({ rule, result: rule.getDeviceIdsForPolling() }));
    const active = evaluated.filter(({ result }) => result.length > 0);
    const deviceIds = [...new Set(active.flatMap(({ result }) => result))];

    try {
        await refreshChangedDevices({
            includeRecentData: true,
            includeUpdatedDevices: true,
            includeSpecificDeviceIDs: deviceIds.length > 0 ? deviceIds : undefined,
        });
    } catch (error) {
        console.error("Error polling devices:", error);
    }
}

/**
 * Run one poll, then schedule the next after it completes.
 * Prevents overlapping polls when refreshChangedDevices takes longer than the frequency.
 * Always reschedules when still running so a single thrown error does not stop the chain.
 */
async function runThenSchedule(): Promise<void> {
    timeoutId = null; // timer has fired; clear stale handle so scheduleNext's dedup guard works correctly
    isPolling = true;
    try {
        await pollDevices();
    } catch (error) {
        console.error("Error in poll cycle (rescheduling to keep polling):", error);
    } finally {
        isPolling = false;
    }
    if (isRunning) {
        // Re-evaluate rules so any changes that arrived mid-poll are reflected.
        try {
            scheduleNext(getHighestActiveFrequency(rules));
        } catch (error) {
            console.error("Error evaluating polling rules after poll (falling back to default interval):", error);

            // Fall back to DEFAULT so the poll chain continues
            scheduleNext(POLLING_FREQUENCIES.DEFAULT);
        }
    }
}

function start(): void {
    if (!isRunning) {
        isRunning = true;
        scheduleNext(getHighestActiveFrequency(rules));
    }
}

// === Public API ===

/**
 * Add a polling rule. Auto-starts the service on the first call.
 */
export function addPollingRule(rule: PollingRule): void {
    if (rules.some((r) => r.id === rule.id)) return;
    rules = [...rules, rule];
    if (!isRunning) start();
    else scheduleNext(getHighestActiveFrequency(rules));
}

/**
 * Remove a polling rule. Stops the service when the last rule is removed.
 */
export function removePollingRule(rule: PollingRule): void {
    rules = rules.filter((r) => r.id !== rule.id);
    if (!isRunning) return;
    if (rules.length === 0) {
        clearScheduledTimeout();
        isRunning = false;
        currentIntervalMs = null;
    } else {
        scheduleNext(getHighestActiveFrequency(rules));
    }
}

/**
 * Re-evaluate active rules and apply the current poll frequency immediately.
 * Call when rule activity may have changed without add/remove (e.g. user opened a card)
 * so the next poll is scheduled at the correct interval without waiting for a tick.
 */
export function recomputePollingInterval(): void {
    scheduleNext(getHighestActiveFrequency(rules));
}
